import type {
  ButtonHTMLAttributes,
  PropsWithChildren,
  ReactNode,
  Ref,
} from 'react'
import { forwardRef, isValidElement } from 'react'

import { theme } from '@procurify/ui-theme'

import { renderIcon } from '../Icon'
import { CircleLoader } from '../Loaders'
import { BaseButtonStyled } from './styles/BaseButtonStyled'
import { ButtonLoaderStyled } from './styles/ButtonLoaderStyled'
import { getAnalyticsDataAttrs } from '@ui/utils/analytics'

type NativeButtonProps = ButtonHTMLAttributes<HTMLButtonElement>

export interface IBaseButtonProps extends NativeButtonProps {
  /**
   * Unique identifier for analytics tracking of button interactions
   */
  analyticsId?: string
  isLoading?: boolean
  iconComponent?: ReactNode
  fullWidth?: boolean
  ref?: Ref<HTMLButtonElement>
  showIconOnEnd?: boolean
}

export const BaseButton = forwardRef<HTMLButtonElement, IBaseButtonProps>(
  (props: PropsWithChildren<IBaseButtonProps>, ref) => {
    const {
      children,
      analyticsId,
      isLoading,
      iconComponent,
      disabled,
      showIconOnEnd,
      ...otherProps
    } = props

    const renderContent = () => {
      if (showIconOnEnd) {
        const iconElement = isValidElement(iconComponent)
          ? renderIcon(iconComponent, { className: 'end-icon' })
          : iconComponent

        return (
          <>
            {children}
            {iconElement}
          </>
        )
      }

      return (
        <>
          {iconComponent}
          {children}
        </>
      )
    }

    const analyticsProps = getAnalyticsDataAttrs(analyticsId)

    return (
      <BaseButtonStyled
        ref={ref}
        disabled={isLoading || disabled}
        {...analyticsProps}
        {...otherProps}
      >
        {renderContent()}

        {isLoading && (
          <ButtonLoaderStyled>
            <CircleLoader color={theme.colors.text.primary} size={14} />
          </ButtonLoaderStyled>
        )}
      </BaseButtonStyled>
    )
  }
)

BaseButton.displayName = 'BaseButton'
